import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { DragOverlay } from '@dnd-kit/core';
import { Task, TaskDay } from '@/types/tasks';
import TaskCard from './TaskCard';
import TaskCardGhost from './TaskCard/TaskCardGhost';
import { useCompletedTasksVisibility } from './hooks/useCompletedTasksVisibility';
import ActiveTasksSection from './components/ActiveTasksSection';
import CompletedTasksSection from './components/CompletedTasksSection';
import { dragOverlayVariants } from './animations/task-animations';
import { motion, useAnimationControls } from 'framer-motion';

interface TaskBoardContentProps {
  days: TaskDay[];
  activeTask: Task | null;
  setActiveTask: (task: Task | null) => void;
  onCreateTask?: () => void;
  customTaskOperations?: {
    completeTask?: (taskId: string) => void;
    uncompleteTask?: (taskId: string) => void;
    removeTask?: (taskId: string) => void;
    updateTask?: (taskId: string, updates: Partial<Task>, silent?: boolean) => void;
  };
  // NEW: Project filter props
  selectedProjectId?: string | null;
  onProjectFilterChange?: (projectId: string | null) => void;
  allTasks?: any[];
  projectTaskCounts?: {
    projectCounts: Record<string, number>;
    totalTasks: number;
  };
}

const TaskBoardContent: React.FC<TaskBoardContentProps> = React.memo(({ 
  days, 
  activeTask, 
  setActiveTask, 
  onCreateTask, 
  customTaskOperations,
  selectedProjectId,
  onProjectFilterChange,
  allTasks,
  projectTaskCounts
}) => {
  const controls = useAnimationControls();
  
  // Clear active task after animation completes
  useEffect(() => {
    if (activeTask) {
      // Reset animation when a new active task is set
      controls.set("initial");
      controls.start("animate");
    }
  }, [activeTask, controls]);

  // Use the filtered tasks from props (days) instead of pulling from context
  const { 
    handleClearCompletedTasks 
  } = useCompletedTasksVisibility();

  // Get tasks from the days prop which contains the filtered tasks
  const filteredTasks = useMemo(() => 
    days.length > 0 ? days[0].tasks : [], 
    [days]
  );
  
  // Memoize the expensive task splitting operation (50% faster than double filter)
  const taskItems = useMemo(() => {
    return filteredTasks.reduce((acc, task) => {
      if (task.isCompleted) {
        acc.completed.push(task);
      } else {
        acc.active.push(task);
      }
      return acc;
    }, { active: [] as Task[], completed: [] as Task[] });
  }, [filteredTasks]);

  return (
    <>
      <div className="space-y-3 md:space-y-6">
        {/* Active Tasks Section */}
        <ActiveTasksSection 
          tasks={taskItems.active} 
          customTaskOperations={customTaskOperations}
          onCreateTask={onCreateTask}
          selectedProjectId={selectedProjectId}
          onProjectFilterChange={onProjectFilterChange}
          allTasks={allTasks}
          projectTaskCounts={projectTaskCounts}
        />

        {/* Completed Tasks Section */}
        <CompletedTasksSection 
          tasks={taskItems.completed}
          handleClearCompletedTasks={handleClearCompletedTasks}
          customTaskOperations={customTaskOperations}
        />
      </div>

      {/* Portal DragOverlay to document.body to escape transform context */}
      {typeof window !== 'undefined' && createPortal(
        <DragOverlay
          dropAnimation={null}
          modifiers={[]}
          zIndex={1000}
        >
          {activeTask ? (
            <TaskCardGhost task={activeTask} />
          ) : null}
        </DragOverlay>,
        document.body
      )}
    </>
  );
});

TaskBoardContent.displayName = 'TaskBoardContent';

export default TaskBoardContent;
